/** Benachrichtigungen (testerwerk#147): jeder Benutzer stellt SELBST ein,
 * welche Ereignisse ihn über welchen Kanal erreichen. Stufe 1: E-Mail;
 * Telegram folgt (Spalte schon sichtbar). Die Adresse kommt aus dem Login. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Bell, Mail, Send } from "lucide-react"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"
import type { BenachrichtigungsEinstellungen } from "@/types"

const KANAL_LABEL: Record<string, string> = { email: "E-Mail", telegram: "Telegram" }

function Schalter({
  an,
  onWechsel,
  gesperrt,
  label,
}: {
  an: boolean
  onWechsel: () => void
  gesperrt?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={an}
      aria-label={label}
      disabled={gesperrt}
      onClick={onWechsel}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        an ? "bg-primary" : "bg-secondary"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          an ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  )
}

export function BenachrichtigungenPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ["benachrichtigungen"],
    queryFn: api.benachrichtigungen.einstellungen,
  })

  const setzen = useMutation({
    mutationFn: (v: { art: string; kanal: string; aktiv: boolean }) =>
      api.benachrichtigungen.setzen(v.art, v.kanal, v.aktiv),
    onMutate: (v) => {
      // sofort umschalten, bei Fehler zurück
      const vorher = queryClient.getQueryData<BenachrichtigungsEinstellungen>([
        "benachrichtigungen",
      ])
      if (vorher) {
        queryClient.setQueryData<BenachrichtigungsEinstellungen>(["benachrichtigungen"], {
          ...vorher,
          arten: vorher.arten.map((a) =>
            a.schluessel === v.art ? { ...a, kanaele: { ...a.kanaele, [v.kanal]: v.aktiv } } : a
          ),
        })
      }
      return { vorher }
    },
    onSuccess: (neu) => queryClient.setQueryData(["benachrichtigungen"], neu),
    onError: (e, _v, ctx) => {
      if (ctx?.vorher) queryClient.setQueryData(["benachrichtigungen"], ctx.vorher)
      toast.error(apiError(e))
    },
  })

  const testMail = useMutation({
    mutationFn: api.benachrichtigungen.testMail,
    onSuccess: (r) => toast.success(`Test-Mail an ${r.an} verschickt`),
    onError: (e) => toast.error(apiError(e)),
  })

  if (isLoading) return <p className="text-muted-foreground">Lade Einstellungen …</p>
  if (error || !data)
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">{apiError(error)}</p>
    )

  const spalten = [...data.kanaele, ...data.kanaeleFolgen]

  return (
    <div className="mx-auto flex max-w-[860px] flex-col gap-5">
      <div>
        <h1 className="mb-1.5 flex items-center gap-2 text-[26px] font-bold tracking-tight">
          <Bell className="h-6 w-6 text-primary" /> Benachrichtigungen
        </h1>
        <p className="text-sm text-muted-foreground">
          Lege fest, wann dich das Testerwerk benachrichtigt — zum Beispiel, wenn deine Antwort
          gebraucht wird. Jede Einstellung gilt sofort.
        </p>
      </div>

      {!data.versandAktiv && (
        <p className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Der Versand ist auf dieser Instanz noch nicht eingeschaltet. Deine Einstellungen werden
          gespeichert und gelten, sobald er aktiv ist.
        </p>
      )}

      <section className="rounded-xl border bg-card p-5 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Benachrichtigungen gehen an</span>
            <span className="font-semibold">{data.email ?? "— keine Adresse bekannt —"}</span>
          </div>
          <button
            type="button"
            onClick={() => testMail.mutate()}
            disabled={!data.email || !data.versandAktiv || testMail.isPending}
            title={
              !data.versandAktiv
                ? "Erst möglich, wenn der Versand auf dieser Instanz eingeschaltet ist"
                : undefined
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            {testMail.isPending ? "Sende …" : "Test-Mail senden"}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Die Adresse stammt aus deiner Anmeldung. Für eine andere Adresse wende dich bitte an
          deinen Ansprechpartner bei netz-fabrik.
        </p>
      </section>

      <section className="overflow-x-auto rounded-xl border bg-card shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-[12px] uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-3 font-bold">Ereignis</th>
              {spalten.map((k) => (
                <th key={k} className="w-28 px-3 py-3 text-center font-bold">
                  {KANAL_LABEL[k] ?? k}
                  {data.kanaeleFolgen.includes(k) && (
                    <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal">
                      folgt
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.arten.map((a) => (
              <tr key={a.schluessel} className="border-b last:border-b-0">
                <td className="px-5 py-3.5">
                  <div className="font-semibold">{a.titel}</div>
                  <div className="text-[12.5px] text-muted-foreground">{a.beschreibung}</div>
                </td>
                {spalten.map((k) => (
                  <td key={k} className="px-3 py-3.5 text-center">
                    {data.kanaeleFolgen.includes(k) ? (
                      <Schalter an={false} gesperrt onWechsel={() => {}} label={`${a.titel} per ${KANAL_LABEL[k] ?? k} (folgt)`} />
                    ) : (
                      <Schalter
                        an={!!a.kanaele[k]}
                        onWechsel={() =>
                          setzen.mutate({ art: a.schluessel, kanal: k, aktiv: !a.kanaele[k] })
                        }
                        label={`${a.titel} per ${KANAL_LABEL[k] ?? k}`}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
